import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { submissionsAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { 
  ArrowLeftIcon,
  DocumentTextIcon,
  LinkIcon,
  CalendarIcon,
  UserIcon,
  TrophyIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { format } from 'date-fns'

const statusIcons = {
  submitted: ClockIcon,
  under_review: ExclamationTriangleIcon,
  evaluated: CheckCircleIcon,
}

const statusColors = {
  submitted: 'text-yellow-600 bg-yellow-100',
  under_review: 'text-blue-600 bg-blue-100',
  evaluated: 'text-green-600 bg-green-100',
}

const statusLabels = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  evaluated: 'Evaluated',
}

export default function SubmissionDetailPage() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  const { data: submissionData, isLoading, error } = useQuery(
    ['submission', id],
    () => submissionsAPI.getById(id),
    {
      select: (response) => response.data.submission
    }
  )

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !submissionData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Submission not found</h2>
          <p className="text-gray-600 mb-4">The submission you're looking for doesn't exist or you don't have access to it.</p>
          <Link to="/submissions" className="btn-primary">
            Back to Submissions
          </Link>
        </div>
      </div>
    )
  }

  const StatusIcon = statusIcons[submissionData.status]

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${statusColors[submissionData.status]}`}>
                <StatusIcon className="h-5 w-5" />
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                submissionData.status === 'evaluated' ? 'bg-green-100 text-green-800' :
                submissionData.status === 'under_review' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {statusLabels[submissionData.status]}
              </span>
            </div>
          </div>
          
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {submissionData.title}
              </h1>
              <div className="mt-2 flex items-center text-sm text-gray-500 space-x-6">
                <div className="flex items-center">
                  <UserIcon className="h-4 w-4 mr-1" />
                  <span>{submissionData.username || `user_${submissionData.user_id}`}</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  <span>Submitted {format(new Date(submissionData.created_at), 'MMM dd, yyyy')}</span>
                </div>
                {submissionData.score && (
                  <div className="flex items-center">
                    <TrophyIcon className="h-4 w-4 mr-1" />
                    <span>Score: {submissionData.score}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
              {submissionData.user_id === user?.id && submissionData.status === 'submitted' && (
                <Link
                  to={`/submissions/${submissionData.id}/edit`}
                  className="btn-outline"
                >
                  Edit Submission
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{submissionData.description}</p>
              </div>
            </div>

            {/* Files */}
            {submissionData.files && submissionData.files.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Submitted Files</h3>
                <div className="space-y-3">
                  {submissionData.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.originalName}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB • {file.mimetype}
                          </p>
                        </div>
                      </div>
                      <button className="text-primary-600 hover:text-primary-500 text-sm">
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            {(submissionData.repository_url || submissionData.demo_url) && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Links</h3>
                <div className="space-y-3">
                  {submissionData.repository_url && (
                    <div className="flex items-center">
                      <LinkIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Repository</p>
                        <a
                          href={submissionData.repository_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 hover:text-primary-500"
                        >
                          {submissionData.repository_url}
                        </a>
                      </div>
                    </div>
                  )}
                  {submissionData.demo_url && (
                    <div className="flex items-center">
                      <LinkIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Demo</p>
                        <a
                          href={submissionData.demo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 hover:text-primary-500"
                        >
                          {submissionData.demo_url}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Feedback */}
            {submissionData.feedback && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Evaluation Feedback</h3>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{submissionData.feedback}</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Challenge Info */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Challenge</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Title</p>
                  <Link
                    to={`/challenges/${submissionData.challenge_id}`}
                    className="text-sm text-primary-600 hover:text-primary-500"
                  >
                    {submissionData.challenge_title}
                  </Link>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Category</p>
                  <p className="text-sm text-gray-900">{submissionData.category}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Difficulty</p>
                  <p className="text-sm text-gray-900 capitalize">{submissionData.difficulty}</p>
                </div>
              </div>
            </div>

            {/* Submission Info */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Submission Details</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="text-sm text-gray-900 capitalize">
                    {statusLabels[submissionData.status]}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Submitted</dt>
                  <dd className="text-sm text-gray-900">
                    {format(new Date(submissionData.created_at), 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="text-sm text-gray-900">
                    {format(new Date(submissionData.updated_at), 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                {submissionData.score && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Score</dt>
                    <dd className="text-sm text-gray-900 font-bold">
                      {submissionData.score}/100
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Author Info */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Author</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Name</p>
                  <p className="text-sm text-gray-900">
                    {submissionData.username || `user_${submissionData.user_id}`}
                  </p>
                </div>
                {submissionData.organization && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Organization</p>
                    <p className="text-sm text-gray-900">{submissionData.organization}</p>
                  </div>
                )}
                {submissionData.country && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Country</p>
                    <p className="text-sm text-gray-900">{submissionData.country}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}